// menuConfig.js
// Each item's `moduleKey` maps to the RolePermission module used to check allowed_devices.
// permission(rp) => whether the user has view rights
// moduleKey => which RolePermission[moduleKey].allowed_devices to check for the current device

import { AiFillDashboard } from "react-icons/ai";
import { BsCart4, BsPatchQuestion, BsCartCheck } from "react-icons/bs";
import {
  MdAssignmentTurnedIn, MdLabel, MdOutlineAutoGraph, MdOutlineCalendarMonth,
  MdOutlineProductionQuantityLimits, MdOutlineDashboard, MdOutlineAnalytics,
  MdLocationCity, MdListAlt, MdOutlineWarehouse, MdOutlineInventory2, MdOutlineRule,
  MdOutlineBuildCircle, MdOutlineBrush, MdOutlineAssignmentTurnedIn, MdOutlineWorkHistory,
  MdOutlineMap, MdOutlineRoute, MdRateReview, MdPrecisionManufacturing,
  MdAutorenew, MdOutlineLocalShipping, MdCompareArrows, MdCompare
} from "react-icons/md";
import { LiaIndustrySolid } from "react-icons/lia";
import { FaRegObjectGroup, FaUserFriends, FaPencilRuler, FaTruckLoading, FaClipboardList, FaUserTie, FaTruck, FaUsersCog, FaCalendarAlt } from "react-icons/fa";
import { RiUserSettingsLine, RiFileList2Line, RiUserSearchLine, RiShieldUserLine } from "react-icons/ri";
import { GiMaterialsScience, GiCardPickup } from "react-icons/gi";
import { AiOutlineNumber, AiOutlineGroup, AiOutlineBarcode } from "react-icons/ai";
import { HiOutlineDocumentText } from "react-icons/hi";
import { IoDocumentTextOutline } from "react-icons/io5";
import { BiSearchAlt } from "react-icons/bi";
import { FaShuttleVan, FaUserCog } from "react-icons/fa";
import { StockIcon, StockProductsIcon } from "./Common/customIcons";

export const menuConfig = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: AiFillDashboard,
    activePaths: ["/dashboard", "/dashboard/operation", "/dashboard/prod-status", "/dashboard/pickup"],
    permission: () => true,
    moduleKey: null,
    items: [
      { path: "/dashboard", label: "Dashboard", icon: MdOutlineDashboard, matchType: "exact", permission: () => true, moduleKey: null },
      { path: "/dashboard/operation", label: "Operations Dashboard", icon: MdOutlineAnalytics, matchType: "exact", permission: (rp) => rp?.Master && rp?.OperationDashboard?.view === "1", moduleKey: "OperationDashboard" },
      { path: "/dashboard/prod-status", label: "Production Status", icon: MdOutlineProductionQuantityLimits, matchType: "exact", permission: (rp) => rp?.Master && rp?.Common?.view === "1", moduleKey: "Common" },
      { path: "/dashboard/pickup", label: "Pickup Dashboard", icon: GiCardPickup, matchType: "exact", permission: (rp) => rp?.Common?.view === "1", moduleKey: "Common" },
      { path: "/dashboard/racking", label: "Racking Dashboard", icon: MdOutlineWarehouse, matchType: "exact", permission: (rp) => rp?.Common?.view === "1", moduleKey: "Common" },
    ],
  },

  {
    key: "stock-management",
    label: "Stock Management",
    icon: StockIcon,
    activePaths: ["/stock"],
    permission: () => true,
    moduleKey: null,
    items: [
      { path: "/stock/dashboard", label: "Dashboard", icon: MdOutlineDashboard, matchType: "exact", permission: () => true, moduleKey: null },
      { path: "/stock/vendors", label: "Suppliers", icon: FaUserFriends, matchType: "exact", permission: (rp) => rp?.StockSuppliers && rp?.StockSuppliers?.view === "1", moduleKey: "StockSuppliers" },
      { path: "/stock/products", label: "Products", icon: StockProductsIcon, matchType: "exact", permission: (rp) => rp?.StockProducts && rp?.StockProducts?.view === "1", moduleKey: "StockProducts" },
      { path: "/stock/purchase", label: "Purchasing", icon: MdOutlineProductionQuantityLimits, matchType: "startsWith", permission: (rp) => rp?.StockPurchase && rp?.StockPurchase?.view === "1", moduleKey: "StockPurchase" },
    ],
  },

  {
    key: "masters",
    label: "Masters",
    icon: FaRegObjectGroup,
    activePaths: ["/customer", "/materials", "/custom-item-code", "/racking", "/item-code-group", "/city", "/calendar", "/logs", "/email-signatures"],
    permission: (rp) => rp?.Master?.view === "1",
    moduleKey: "Master",
    items: [
      { path: "/customer", label: "Customers", icon: FaUserFriends, matchType: "startsWith", permission: (rp) => rp?.Customer?.view === "1", moduleKey: "Customer" },
      { path: "/materials", label: "Materials", icon: GiMaterialsScience, matchType: "startsWith", permission: (rp) => rp?.Product?.view === "1", moduleKey: "Product" },
      { path: "/custom-item-code", label: "Custom Item Code", icon: AiOutlineNumber, matchType: "exact", permission: (rp) => rp?.CustomItemMaster?.view === "1", moduleKey: "CustomItemMaster" },
      { path: "/item-code-group", label: "Item Code Group", icon: AiOutlineGroup, matchType: "startsWith", permission: (rp) => rp?.Master?.view === "1", moduleKey: "Master" },
      { path: "/racking", label: "Racks", icon: MdOutlineInventory2, matchType: "exact", permission: (rp) => rp?.Master?.view === "1", moduleKey: "Master" },
      { path: "/city", label: "City", icon: MdLocationCity, matchType: "exact", permission: (rp) => rp?.Master?.view === "1", moduleKey: "Master" },
      { path: "/calendar", label: "Calendar", icon: MdOutlineCalendarMonth, matchType: "exact", permission: (rp) => rp?.Master?.view === "1", moduleKey: "Master" },
      { path: "/email-signatures", label: "Email Signatures", icon: MdRateReview, matchType: "exact", permission: (rp) => rp?.Master?.view === "1", moduleKey: "Master" },
      { path: "/logs", label: "Logs", icon: MdListAlt, matchType: "exact", permission: (rp) => rp?.Master?.view === "1", moduleKey: "Master" },
    ],
  },

  {
    key: "quotations",
    label: "Quotations",
    icon: BsPatchQuestion,
    activePaths: ["/quotation/master", "/quotation/designers", "/quotation/quality-checking"],
    permission: () => true,
    moduleKey: "OrderManagement",
    items: [
      { path: "/quotation/master", label: "Quotation Master", icon: HiOutlineDocumentText, matchType: "startsWith", permission: (rp) => rp?.OrderManagement?.view === "1", moduleKey: "OrderManagement" },
      { path: "/quotation/designers", label: "Designers Management", icon: FaPencilRuler, matchType: "startsWith", permission: (rp) => rp?.DesignerModule?.view === "1", moduleKey: "DesignerModule" },
      { path: "/quotation/quality-checking", label: "QC Management", icon: MdOutlineRule, matchType: "startsWith", permission: (rp) => rp?.DesignerModule?.view === "1", moduleKey: "DesignerModule" },
    ],
  },

  {
    key: "orders",
    label: "Orders",
    icon: BsCart4,
    activePaths: ["/orders", "/designers", "/quality-checking", "/manage-panels"],
    permission: () => true,
    moduleKey: "OrderManagement",
    items: [
      { path: "/orders", label: "Master Orders", icon: BsCartCheck, matchType: "startsWith", permission: (rp) => rp?.OrderManagement?.view === "1", moduleKey: "OrderManagement" },
      { path: "/designers", label: "Designers Management", icon: FaPencilRuler, matchType: "startsWith", permission: (rp) => rp?.DesignerModule?.view === "1", moduleKey: "DesignerModule" },
      { path: "/quality-checking", label: "QC Management", icon: MdOutlineRule, matchType: "startsWith", permission: (rp) => rp?.DesignerModule?.view === "1", moduleKey: "DesignerModule" },
      { path: "/manage-panels", label: "Cladding Management", icon: MdAssignmentTurnedIn, matchType: "startsWith", permission: (rp) => rp?.CladdingModule?.view === "1", moduleKey: "CladdingModule" },
    ],
  },

  {
    key: "production",
    label: "Production",
    icon: LiaIndustrySolid,
    activePaths: ["/production", "/reports/rack-management", "/generate-barcode"],
    permission: () => true,
    moduleKey: "Production",
    items: [
      { path: "/production", label: "Barcode Scanning", icon: AiOutlineBarcode, matchType: "startsWith", permission: (rp) => rp?.Production?.view === "1", moduleKey: "Production" },
      { path: "/reports/rack-management", label: "Rack Updation", icon: MdOutlineBuildCircle, matchType: "startsWith", permission: () => true, moduleKey: null },
      { path: "/generate-barcode", label: "Barcode Generator", icon: MdLabel, matchType: "startsWith", permission: (rp) => rp?.BarcodeGenerator?.view === "1", moduleKey: "BarcodeGenerator" },
    ],
  },

  {
    key: "reports",
    label: "Reports",
    icon: MdOutlineAutoGraph,
    activePaths: [
      "/reports/order-reports", "/reports/designer-reports", "/reports/qc-reports",
      "/reports/production-reports", "/reports/production-emp-work-info",
      "/reports/swi-machine", "/reports/remake-order-report", "/reports/customer-reports", "/reports/exact-item-to-dispatch-report",
    ],
    permission: () => true,
    moduleKey: "OrderReport",
    items: [
      { path: "/reports/order-reports", label: "Order Entry Report", icon: RiFileList2Line, matchType: "startsWith", permission: (rp) => rp?.Master && rp?.OrderReport?.view === "1", moduleKey: "OrderReport" },
      { path: "/reports/designer-reports", label: "Designer Reports", icon: MdOutlineBrush, matchType: "startsWith", permission: (rp) => rp?.Master && rp?.DesignReport?.view === "1", moduleKey: "DesignReport" },
      { path: "/reports/qc-reports", label: "QC Reports", icon: MdOutlineAssignmentTurnedIn, matchType: "startsWith", permission: (rp) => rp?.Master && rp?.DesignReport?.view === "1", moduleKey: "QCReport" },
      { path: "/reports/production-reports", label: "Production Emp Reports", icon: FaUserCog, matchType: "startsWith", permission: (rp) => rp?.Master && rp?.ProductionReport?.view === "1", moduleKey: "ProductionReport" },
      { path: "/reports/production-emp-work-info", label: "Prod Emp Work Info", icon: MdOutlineWorkHistory, matchType: "startsWith", permission: (rp) => rp?.Production?.view === "1", moduleKey: "Production" },
      { path: "/reports/customer-reports", label: "Customer Reports", icon: RiUserSearchLine, matchType: "startsWith", permission: (rp) => rp?.Master && rp?.CustomerReport?.view === "1", moduleKey: "CustomerReport" },
      { path: "/reports/swi-machine", label: "SWI Machine Report", icon: MdPrecisionManufacturing, matchType: "exact", permission: (rp) => rp?.Master && rp?.SWIMachineReport?.view === "1", moduleKey: "SWIMachineReport" },
      { path: "/reports/remake-order-report", label: "Remake Order Report", icon: MdAutorenew, matchType: "exact", permission: (rp) => rp?.Master && rp?.RemakeOrderReport?.view === "1", moduleKey: "RemakeOrderReport" },
      { path: "/reports/exact-item-to-dispatch-report", label: "Exact Item To Dispatch Report", icon: MdOutlineLocalShipping, matchType: "exact", permission: (rp) => rp?.Master && rp?.ExactItemsToDispatch?.view === "1", moduleKey: "ExactItemsToDispatchReport" },
    ],
  },

  {
    key: "deliveryPlanning",
    label: "Delivery Planning",
    icon: MdOutlineMap,
    activePaths: [
      "/del-planning", "/del-planning/ext-orders",
      "/del-planning/delivery-docket", "/del-planning/critical", "/del-planning/compare", "/del-planning/map",
    ],
    permission: (rp) => rp?.DeliveryPlanning?.view === "1",
    moduleKey: "DeliveryPlanning",
    items: [
      { path: "/del-planning", label: "Planning Dashboard", icon: FaCalendarAlt, matchType: "exact", permission: () => true, moduleKey: "DeliveryPlanning" },
      { path: "/del-planning/critical", label: "Critical Planning Dashboard", icon: FaCalendarAlt, matchType: "startsWith", permission: () => true, moduleKey: "DeliveryPlanning" },
      { path: "/del-planning/compare", label: "Report Compare", icon: MdCompare, matchType: "startsWith", permission: () => true, moduleKey: "DeliveryPlanning" },
      { path: "/del-planning/map", label: "Delivery Map", icon: MdOutlineMap, matchType: "startsWith", permission: () => true, moduleKey: "DeliveryPlanning" },
      { path: "/del-planning/ext-orders", label: "External Orders", icon: FaTruckLoading, matchType: "startsWith", permission: () => true, moduleKey: "DeliveryPlanning" },
      // { path: "/reports/delivery-reports", label: "Delivery Reports", icon: FaClipboardList, matchType: "startsWith", permission: (rp) => rp?.Master && rp?.DeliveryReport?.view === "1", moduleKey: "DeliveryReport" },
      { path: "/del-planning/delivery-docket", label: "Delivery Docket", icon: IoDocumentTextOutline, matchType: "startsWith", permission: (rp) => rp?.Master && rp?.DeliveryDocket?.view === "1", moduleKey: "DeliveryDocket" },
      { path: "/del-planning/en-to-awf", label: "Encore To AWF", icon: MdCompareArrows, matchType: "startsWith", permission: () => true, moduleKey: "DeliveryPlanning" },
    ],
  },

  {
    key: "transport",
    label: "Transport",
    icon: FaShuttleVan,
    activePaths: ["/transport/runs", "/reports/loading-reports", "/transport/drivers", "/transport/trucks"],
    permission: (rp) => rp?.RunsManagement?.view === "1",
    moduleKey: "RunsManagement",
    items: [
      { path: "/transport/runs", label: "Manage Runs", icon: MdOutlineRoute, matchType: "startsWith", permission: () => true, moduleKey: "RunsManagement" },
      { path: "/reports/loading-reports", label: "Find My Rack", icon: BiSearchAlt, matchType: "startsWith", permission: (rp) => rp?.Master && rp?.LoadingReport?.view === "1", moduleKey: "LoadingReport" },
      { path: "/transport/drivers", label: "Manage Drivers", icon: FaUserTie, matchType: "startsWith", permission: () => true, moduleKey: "RunsManagement" },
      { path: "/transport/trucks", label: "Manage Trucks", icon: FaTruck, matchType: "startsWith", permission: () => true, moduleKey: "RunsManagement" },
    ],
  },

  {
    key: "users",
    label: "Users",
    icon: RiUserSettingsLine,
    activePaths: ["/users", "/users/roles"],
    permission: (rp) => rp?.UserManagement?.view === "1",
    moduleKey: "UserManagement",
    items: [
      { path: "/users", label: "Manage Users", icon: FaUsersCog, matchType: "exact", permission: () => true, moduleKey: "UserManagement" },
      { path: "/users/roles", label: "Manage Roles", icon: RiShieldUserLine, matchType: "exact", permission: () => true, moduleKey: "RolesManagement" },
    ],
  },

  {
    key: "loaderCopy",
    label: "Loader Copy",
    icon: FaTruckLoading,
    activePaths: ["/loader-copy"],
    permission: (rp) => rp?.RunsManagement?.view === "1",
    moduleKey: "RunsManagement",
    items: [
      { path: "/loader-copy", label: "Loader Copy", icon: FaTruck, matchType: "exact", permission: () => true, moduleKey: "RunsManagement" },
    ],
  },
];

export const singleMenuItems = [
  {
    key: "orderCoordinator",
    label: "Order Coordinator",
    icon: MdRateReview,
    path: "/msg",
    activePaths: ["/msg"],
    permission: (rp) => rp?.OrderCoordinator?.view === "1",
    moduleKey: "OrderCoordinator",
  },
];